"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, BarChart3, Building2, Gauge, KeyRound, LayoutDashboard, LogOut, Mail, Menu, MessageCircle, MessageSquare, Percent, Route, Settings, ShieldAlert, Wallet } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser";
import { useAuth } from "@/lib/auth-client";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /**
   * "prefix" highlights the entry on its detail routes too; "exact" keeps the
   * highlight on the index page only.
   */
  match: "exact" | "prefix";
}

// The catalogue traffic views — /providers, /models, /model-provider-mappings
// and /unstable-mappings — are deliberately unlisted: this deployment sells the
// public model catalogue, so their per-provider internals are not day-to-day
// admin work. The routes still resolve for anyone who has a link.
const navItems: NavItem[] = [{
  href: "/",
  label: "Dashboard",
  icon: LayoutDashboard,
  match: "exact"
}, {
  href: "/organizations",
  label: "Organizations",
  icon: Building2,
  match: "prefix"
}, {
  href: "/chat-plans",
  label: "Lounge Plans",
  icon: MessageSquare,
  match: "prefix"
}, {
  href: "/global-stats",
  label: "Global Stats",
  icon: BarChart3,
  match: "prefix"
}, {
  href: "/discounts",
  label: "Discounts",
  icon: Percent,
  match: "exact"
}, {
  href: "/rate-limits",
  label: "Global Rate Limits",
  icon: Gauge,
  match: "exact"
}, {
  href: "/provider-credentials",
  label: "Provider Credentials",
  icon: KeyRound,
  match: "prefix"
}, {
  href: "/routing-analytics",
  label: "Routing Analytics",
  icon: Route,
  match: "prefix"
}, {
  href: "/contact-submissions",
  label: "Contact Submissions",
  icon: Mail,
  match: "prefix"
}, {
  href: "/provider-listing-requests",
  label: "Provider Requests",
  icon: Building2,
  match: "prefix"
}, {
  href: "/chat-support-logs",
  label: "Chat Support Logs",
  icon: MessageCircle,
  match: "prefix"
}, {
  href: "/payment-failures",
  label: "Payment Failures",
  icon: AlertTriangle,
  match: "prefix"
}, {
  href: "/payout-wallets",
  label: "Payout Wallets",
  icon: Wallet,
  match: "prefix"
}, {
  href: "/flagged-accounts",
  label: "Flagged Accounts",
  icon: ShieldAlert,
  match: "prefix"
}, {
  href: "/limit-hits",
  label: "Limit Hits",
  icon: ShieldAlert,
  match: "prefix"
}, {
  href: "/settings",
  label: "Settings",
  icon: Settings,
  match: "exact"
}];
function isActive(item: NavItem, pathname: string): boolean {
  if (item.href === "/") {
    return pathname === "/" || pathname === "";
  }
  return item.match === "exact" ? pathname === item.href : pathname.startsWith(item.href);
}
interface AdminShellProps {
  children: ReactNode;
  /**
   * Server-rendered session-cookie presence. Used only as the fallback while
   * `/user/me` is still in flight, so the navigation does not flash in or out
   * on every full page load.
   */
  signedIn: boolean;
}
function MobileHeader() {
  const $ = _c(8);
  const {
    toggleSidebar
  } = useSidebar();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Menu className="h-5 w-5" />;
    t1 = <span className="sr-only">Toggle menu</span>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== toggleSidebar) {
    t2 = <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleSidebar}>{t0}{t1}</Button>;
    $[2] = toggleSidebar;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col"><span className="flex h-6 w-[72px] items-center"><Image alt="OffRail" className="-ml-0.5 h-full w-full object-contain object-left dark:hidden" height={251} src="/brand/offrail-wordmark-film-reel-light.png" width={696} /><Image alt="" aria-hidden={true} className="-ml-2 hidden h-full w-full object-contain object-left dark:block" height={341} src="/brand/offrail-wordmark-film-reel.png" width={1024} /></span><span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div>;
    t4 = <ThemeToggle size="compact" className="ml-auto" />;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2) {
    t5 = <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border/60 bg-background px-4 md:hidden">{t2}{t3}{t4}</header>;
    $[6] = t2;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function AdminShell(t0) {
  const $ = _c(28);
  const {
    children,
    signedIn
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    user,
    isLoading,
    error
  } = useUser();
  const showNav = pathname === "/login" ? false : user ? user.isAdmin : isLoading && !error && signedIn;
  if (!showNav) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="absolute right-4 top-4 z-50"><ThemeToggle size="compact" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== children) {
      t2 = <div className="relative min-h-svh w-full">{t1}{children}</div>;
      $[1] = children;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] !== queryClient || $[4] !== router || $[5] !== signOut) {
    t1 = async () => {
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push("/login");
          }
        }
      });
    };
    $[3] = queryClient;
    $[4] = router;
    $[5] = signOut;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const handleSignOut = t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex flex-col px-1 group-data-[collapsible=icon]:hidden"><span className="flex h-6 w-[72px] items-center"><Image alt="OffRail" className="-ml-0.5 h-full w-full object-contain object-left dark:hidden" height={251} src="/brand/offrail-wordmark-film-reel-light.png" width={696} /><Image alt="" aria-hidden={true} className="-ml-2 hidden h-full w-full object-contain object-left dark:block" height={341} src="/brand/offrail-wordmark-film-reel.png" width={1024} /></span><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SidebarHeader className="border-b border-sidebar-border/60"><div className="flex h-12 items-center justify-between px-2 group-data-[collapsible=icon]:h-auto group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-1 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-1">{t2}<span className="hidden aspect-square size-8 items-center justify-center group-data-[collapsible=icon]:flex"><Image alt="" aria-hidden={true} className="size-6 object-contain" height={256} src="/brand/offrail-film-reel-mark.png" width={256} /></span><SidebarTrigger className="hidden md:flex" /></div></SidebarHeader>;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SidebarGroupLabel>Main</SidebarGroupLabel>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== pathname) {
    t5 = navItems.map(item => {
      const Icon = item.icon;
      return <SidebarMenuItem key={item.href}><SidebarMenuButton asChild={true} isActive={isActive(item, pathname)} size="lg" tooltip={item.label} className="group-data-[collapsible=icon]:justify-center"><Link href={item.href} prefetch={true}><Icon className="h-4 w-4" /><span className="group-data-[collapsible=icon]:hidden">{item.label}</span></Link></SidebarMenuButton></SidebarMenuItem>;
    });
    $[10] = pathname;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <SidebarContent><SidebarGroup>{t4}<SidebarMenu>{t5}</SidebarMenu></SidebarGroup></SidebarContent>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex justify-center group-data-[collapsible=icon]:hidden"><ThemeToggle size="compact" /></div>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <LogOut className="h-3.5 w-3.5" />;
    t9 = <span className="group-data-[collapsible=icon]:hidden">Sign out</span>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== handleSignOut) {
    t10 = <SidebarFooter className="border-t border-sidebar-border/60">{t7}<Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs text-muted-foreground group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0" onClick={handleSignOut}>{t8}{t9}</Button></SidebarFooter>;
    $[17] = handleSignOut;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <Sidebar variant="inset" collapsible="icon">{t3}{t6}{t10}</Sidebar>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <MobileHeader />;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== children) {
    t13 = <SidebarInset>{t12}{children}</SidebarInset>;
    $[23] = children;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t13) {
    t14 = <SidebarProvider>{t11}{t13}</SidebarProvider>;
    $[25] = t11;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
}